export interface CandlePatternInfo {
  name: string;
  displayName: string;
  image: string;
  description: string;
  formation: string;
  marketSignal: string;
  predictivePower: string;
  reliability: "High" | "Medium" | "Low";
  type: "Bullish" | "Bearish" | "Neutral";
}

export const candlePatternDatabase: Record<string, CandlePatternInfo> = {
  HAMMER: {
    name: "HAMMER",
    displayName: "Hammer",
    image: "/images/candles/hammer.svg",
    description:
      "A bullish reversal candle that often appears after a fall. It has a small body and a long lower wick.",
    formation:
      "Small body near the top of the range with a long lower wick at least twice the body size and little to no upper wick.",
    marketSignal:
      "Sellers pushed price down but buyers pulled it back. This can signal selling pressure is weakening.",
    predictivePower:
      "Medium to High. Stronger if the next candle closes higher and the prior trend was clearly down.",
    reliability: "High",
    type: "Bullish",
  },
  INVERTED_HAMMER: {
    name: "INVERTED_HAMMER",
    displayName: "Inverted Hammer",
    image: "/images/candles/inverted-hammer.svg",
    description:
      "A bullish reversal candle after a fall, with a long upper wick.",
    formation:
      "Small body near the bottom of the range with a long upper wick and little to no lower wick.",
    marketSignal:
      "Buyers tried to lift price and sellers could not keep it down. Early hint of a turn.",
    predictivePower:
      "Medium. Needs confirmation from the next candle closing higher.",
    reliability: "Medium",
    type: "Bullish",
  },
  DOJI: {
    name: "DOJI",
    displayName: "Doji",
    image: "/images/candles/doji.svg",
    description:
      "Open and close are almost the same. The market is undecided.",
    formation:
      "Very small body with wicks that can be long or short.",
    marketSignal:
      "Momentum is pausing. After a strong move it can hint at a change.",
    predictivePower:
      "Medium. Use with the trend and wait for confirmation.",
    reliability: "Medium",
    type: "Neutral",
  },
  BULLISH_MARUBOZU: {
    name: "BULLISH_MARUBOZU",
    displayName: "Bullish Marubozu",
    image: "/images/candles/bullish-marubozu.svg",
    description:
      "A strong bullish candle with little or no wicks.",
    formation:
      "Opens near the low and closes near the high, forming a solid body.",
    marketSignal:
      "Buyers controlled the session from start to finish.",
    predictivePower:
      "High, especially after a decline or a breakout.",
    reliability: "High",
    type: "Bullish",
  },
  BEARISH_MARUBOZU: {
    name: "BEARISH_MARUBOZU",
    displayName: "Bearish Marubozu",
    image: "/images/candles/bearish-marubozu.svg",
    description:
      "A strong bearish candle with little or no wicks.",
    formation:
      "Opens near the high and closes near the low, forming a solid body.",
    marketSignal:
      "Sellers controlled the session from start to finish.",
    predictivePower:
      "High, especially after a rise or a breakdown.",
    reliability: "High",
    type: "Bearish",
  },
  HANGMAN: {
    name: "HANGMAN",
    displayName: "Hanging Man",
    image: "/images/candles/hangman.svg",
    description:
      "A bearish reversal candle that looks like a hammer but appears after a rise.",
    formation:
      "Small body near the top with a long lower wick.",
    marketSignal:
      "Selling appeared during an uptrend and buyers had to fight back.",
    predictivePower:
      "Medium to High with a lower close on the next candle.",
    reliability: "High",
    type: "Bearish",
  },
  HANGING_MAN: {
    name: "HANGING_MAN",
    displayName: "Hanging Man",
    image: "/images/candles/hangman.svg",
    description:
      "A bearish reversal candle after an uptrend, with a small body and a long lower wick.",
    formation:
      "Small body near the top of the range, long lower wick (at least 2x the body), little to no upper wick.",
    marketSignal:
      "Selling pressure appeared during the uptrend, hinting that buyers may be losing control.",
    predictivePower:
      "Medium to High with confirmation from the next candle closing lower.",
    reliability: "High",
    type: "Bearish",
  },
  SHOOTING_STAR: {
    name: "SHOOTING_STAR",
    displayName: "Shooting Star",
    image: "/images/candles/shooting-star.svg",
    description:
      "A bearish reversal candle after a rise with a long upper wick.",
    formation:
      "Small body near the low with a long upper wick.",
    marketSignal:
      "Buyers pushed price up but were rejected. Momentum may be fading.",
    predictivePower:
      "Medium to High with confirmation.",
    reliability: "High",
    type: "Bearish",
  },
  ENGULFING_BULL: {
    name: "ENGULFING_BULL",
    displayName: "Bullish Engulfing",
    image: "/images/candles/bullish-engulfing.svg",
    description:
      "Two candles where a strong bullish candle fully covers the prior bearish candle.",
    formation:
      "Small bearish candle followed by a larger bullish candle that wraps it.",
    marketSignal:
      "Buyers took control and reversed the move.",
    predictivePower:
      "High, especially after a clear downtrend.",
    reliability: "High",
    type: "Bullish",
  },
  ENGULFING_BEAR: {
    name: "ENGULFING_BEAR",
    displayName: "Bearish Engulfing",
    image: "/images/candles/bearish-engulfing.svg",
    description:
      "Two candles where a strong bearish candle fully covers the prior bullish candle.",
    formation:
      "Small bullish candle followed by a larger bearish candle that wraps it.",
    marketSignal:
      "Sellers took control and reversed the move.",
    predictivePower:
      "High, especially after a clear uptrend.",
    reliability: "High",
    type: "Bearish",
  },
  MORNING_STAR: {
    name: "MORNING_STAR",
    displayName: "Morning Star",
    image: "/images/candles/morning-star.svg",
    description:
      "A three-candle bullish reversal pattern after a fall.",
    formation:
      "Bearish candle, then a small indecision candle, then a strong bullish candle.",
    marketSignal:
      "Selling weakens and buyers step in.",
    predictivePower:
      "High when the third candle closes well into the first candle.",
    reliability: "High",
    type: "Bullish",
  },
  EVENING_STAR: {
    name: "EVENING_STAR",
    displayName: "Evening Star",
    image: "/images/candles/evening-star.svg",
    description:
      "A three-candle bearish reversal pattern after a rise.",
    formation:
      "Bullish candle, then a small indecision candle, then a strong bearish candle.",
    marketSignal:
      "Buying weakens and sellers step in.",
    predictivePower:
      "High when the third candle closes well into the first candle.",
    reliability: "High",
    type: "Bearish",
  },
  HARAMI_BULL: {
    name: "HARAMI_BULL",
    displayName: "Bullish Harami",
    image: "/images/candles/bullish-harami.svg",
    description:
      "Two candles where a small bullish candle sits inside a larger bearish candle.",
    formation:
      "Large bearish candle followed by a small bullish candle inside its range.",
    marketSignal:
      "Selling is slowing and buyers are starting to appear.",
    predictivePower:
      "Medium. Needs confirmation from the next candle.",
    reliability: "Medium",
    type: "Bullish",
  },
  HARAMI_BEAR: {
    name: "HARAMI_BEAR",
    displayName: "Bearish Harami",
    image: "/images/candles/bearish-harami.svg",
    description:
      "Two candles where a small bearish candle sits inside a larger bullish candle.",
    formation:
      "Large bullish candle followed by a small bearish candle inside its range.",
    marketSignal:
      "Buying is slowing and sellers are starting to appear.",
    predictivePower:
      "Medium. Needs confirmation from the next candle.",
    reliability: "Medium",
    type: "Bearish",
  },
  BEARISH_HARAMI: {
    name: "BEARISH_HARAMI",
    displayName: "Bearish Harami",
    image: "/images/candles/bearish-harami.svg",
    description:
      "Two candles where a small bearish candle sits inside a larger bullish candle.",
    formation:
      "Large bullish candle followed by a small bearish candle inside its range.",
    marketSignal:
      "Buying is slowing and sellers are starting to appear.",
    predictivePower:
      "Medium. Needs confirmation from the next candle.",
    reliability: "Medium",
    type: "Bearish",
  },
  BULLISH_DOJI_MA_RECLAIM: {
    name: "BULLISH_DOJI_MA_RECLAIM",
    displayName: "Bullish Doji MA Reclaim",
    image: "/images/candles/doji.svg",
    description:
      "A doji that appears after recent red candles, lines up with a key moving average, and carries supportive volume behavior.",
    formation:
      "Recent candles lean bearish, sell-side volume starts fading or the doji still holds strong participation, and the doji touches the EMA20 or EMA50 before closing back above it.",
    marketSignal:
      "Selling pressure is fading and price is trying to reclaim dynamic support. The doji shows hesitation from sellers around the moving average with volume confirming the stall or absorption.",
    predictivePower:
      "Medium. Stronger when the next candle closes higher and price holds above the reclaimed moving average.",
    reliability: "Medium",
    type: "Bullish",
  },
  BEARISH_DOJI_MA_REJECTION: {
    name: "BEARISH_DOJI_MA_REJECTION",
    displayName: "Bearish Doji MA Rejection",
    image: "/images/candles/doji.svg",
    description:
      "A doji that appears after recent green candles, lines up with a key moving average, and carries supportive volume behavior.",
    formation:
      "Recent candles lean bullish, buy-side volume starts fading or the doji still holds strong participation, and the doji touches the EMA20 or EMA50 before closing back below it.",
    marketSignal:
      "Buying pressure is stalling and price is failing at dynamic resistance. The doji shows indecision as sellers defend the moving average with volume backing the rejection.",
    predictivePower:
      "Medium. Stronger when the next candle closes lower and price stays below the rejecting moving average.",
    reliability: "Medium",
    type: "Bearish",
  },
  BEARISH_LIQUIDITY_GRAB: {
    name: "BEARISH_LIQUIDITY_GRAB",
    displayName: "Bearish Liquidity Grab",
    image: "/images/candles/bearish-liquidity-grab.svg",
    description:
      "A sweep above a prior swing high that quickly fails and closes back below supply.",
    formation:
      "Price wicks above a recent pivot high, then closes below that level with a bearish candle.",
    marketSignal:
      "Stop orders were triggered and absorbed. Sellers regained control at supply.",
    predictivePower:
      "High when it occurs in a clear downtrend and near a strong supply zone.",
    reliability: "High",
    type: "Bearish",
  },
  BEARISH_DOUBLE_TOP: {
    name: "BEARISH_DOUBLE_TOP",
    displayName: "Bearish Double Top Pullback",
    image: "/images/candles/bearish-double-top.svg",
    description:
      "A retest of the prior swing high that fails, followed by a breakdown below the pullback low.",
    formation:
      "Two similar highs form within a tight tolerance, then price breaks the neckline low.",
    marketSignal:
      "Buyers failed to push higher on the retest. Momentum shifts back down.",
    predictivePower:
      "High when the neckline breaks with strong bearish candles.",
    reliability: "High",
    type: "Bearish",
  },
  BEARISH_BREAK_BLOCK: {
    name: "BEARISH_BREAK_BLOCK",
    displayName: "Bearish Break-Block Reversal",
    image: "/images/candles/bearish-break-block.svg",
    description:
      "Price revisits the last bullish candle before a sharp drop and rejects from that zone.",
    formation:
      "A strong bearish impulse is followed by a pullback into the prior bullish candle body, then a bearish close below it.",
    marketSignal:
      "The order block acts as supply. Rejection suggests continuation lower.",
    predictivePower:
      "High when the pullback is shallow and rejection is decisive.",
    reliability: "High",
    type: "Bearish",
  },
  BULLISH_HARAMI: {
    name: "BULLISH_HARAMI",
    displayName: "Bullish Harami",
    image: "/images/candles/bullish-harami.svg",
    description:
      "Two candles where a small bullish candle sits inside a larger bearish candle.",
    formation:
      "Large bearish candle followed by a small bullish candle inside its range.",
    marketSignal:
      "Selling is slowing and buyers are starting to appear.",
    predictivePower:
      "Medium. Needs confirmation from the next candle.",
    reliability: "Medium",
    type: "Bullish",
  },
  DARK_CLOUD_COVER: {
    name: "DARK_CLOUD_COVER",
    displayName: "Dark Cloud Cover",
    image: "/images/candles/dark-cloud-cover.svg",
    description:
      "A two-candle bearish reversal pattern after a rise.",
    formation:
      "Strong bullish candle, then a bearish candle that opens higher but closes below the middle of the first.",
    marketSignal:
      "Buyers lost control quickly and sellers pushed price down.",
    predictivePower:
      "High when it appears after a strong uptrend.",
    reliability: "High",
    type: "Bearish",
  },
  PIERCING_PATTERN: {
    name: "PIERCING_PATTERN",
    displayName: "Piercing Pattern",
    image: "/images/candles/piercing-pattern.svg",
    description:
      "A two-candle bullish reversal pattern after a fall.",
    formation:
      "Strong bearish candle, then a bullish candle that opens lower but closes above the middle of the first.",
    marketSignal:
      "Sellers lost control and buyers pushed price back up.",
    predictivePower:
      "High when it appears after a strong downtrend.",
    reliability: "High",
    type: "Bullish",
  },
  RISING_THREE_METHODS: {
    name: "RISING_THREE_METHODS",
    displayName: "Rising Three Methods",
    image: "/images/candles/unknown.svg",
    description:
      "A bullish continuation pattern during an uptrend.",
    formation:
      "A strong bullish candle, several small pullback candles, then another bullish candle that resumes the uptrend.",
    marketSignal:
      "Brief consolidation before buyers continue the trend.",
    predictivePower:
      "Medium to High when the trend is already strong.",
    reliability: "Medium",
    type: "Bullish",
  },
  FALLING_THREE_METHODS: {
    name: "FALLING_THREE_METHODS",
    displayName: "Falling Three Methods",
    image: "/images/candles/unknown.svg",
    description:
      "A bearish continuation pattern during a downtrend.",
    formation:
      "A strong bearish candle, several small bounce candles, then another bearish candle that resumes the downtrend.",
    marketSignal:
      "Brief consolidation before sellers continue the trend.",
    predictivePower:
      "Medium to High when the trend is already strong.",
    reliability: "Medium",
    type: "Bearish",
  },
  NORMAL_BULL: {
    name: "NORMAL_BULL",
    displayName: "Bullish Candle",
    image: "/images/candles/normal-bull.svg",
    description:
      "A regular up candle where price closed higher than it opened.",
    formation:
      "Close is above the open; wicks may be present.",
    marketSignal: "Buyers were a bit stronger during this period.",
    predictivePower:
      "Low on its own. Stronger when repeated or within an uptrend.",
    reliability: "Low",
    type: "Bullish",
  },
  NORMAL_BEAR: {
    name: "NORMAL_BEAR",
    displayName: "Bearish Candle",
    image: "/images/candles/normal-bear.svg",
    description:
      "A regular down candle where price closed lower than it opened.",
    formation:
      "Close is below the open; wicks may be present.",
    marketSignal: "Sellers were a bit stronger during this period.",
    predictivePower:
      "Low on its own. Stronger when repeated or within a downtrend.",
    reliability: "Low",
    type: "Bearish",
  },
  SPINNING_TOP: {
    name: "SPINNING_TOP",
    displayName: "Spinning Top",
    image: "/images/candles/spinning-top.svg",
    description:
      "A small body with long wicks, showing a tug of war.",
    formation:
      "Small body in the middle with long upper and lower wicks.",
    marketSignal:
      "Indecision. Momentum may pause or change.",
    predictivePower:
      "Low to Medium. Wait for the next candle to confirm.",
    reliability: "Low",
    type: "Neutral",
  },
  UNKNOWN: {
    name: "UNKNOWN",
    displayName: "Flat / No Volume",
    image: "/images/candles/unknown.svg",
    description:
      "Very little movement or no trading activity.",
    formation:
      "Tiny body and tiny wicks, sometimes nearly flat.",
    marketSignal:
      "Low activity. The market is quiet and waiting.",
    predictivePower:
      "Low. Often followed by a bigger move later.",
    reliability: "Low",
    type: "Neutral",
  },
  INSIDE_BAR: {
    name: "INSIDE_BAR",
    displayName: "Inside Bar",
    image: "/images/candles/inside-bar.svg",
    description:
      "A candle that sits fully inside the previous candle's range.",
    formation:
      "High and low are both inside the prior candle.",
    marketSignal:
      "The market is pausing. A breakout can follow.",
    predictivePower:
      "High when it follows a strong move.",
    reliability: "High",
    type: "Neutral",
  },
  OUTSIDE_BAR: {
    name: "OUTSIDE_BAR",
    displayName: "Outside Bar",
    image: "/images/candles/outside-bar.svg",
    description:
      "A candle that breaks both the previous high and the previous low.",
    formation:
      "High is higher and low is lower than the prior candle.",
    marketSignal:
      "Volatility expanded and a strong move may be starting.",
    predictivePower:
      "High. Direction depends on where it closes.",
    reliability: "High",
    type: "Neutral",
  },
};

export const getCandlePatternInfo = (
  patternName: string
): CandlePatternInfo | null => {
  if (!patternName) return null;
  
  // Normalize: convert to uppercase and replace spaces with underscores
  const normalized = patternName.toUpperCase().replace(/\s+/g, "_");
  return candlePatternDatabase[normalized] || null;
};

export const getAllPatterns = (): CandlePatternInfo[] => {
  return Object.values(candlePatternDatabase);
};
